/**
 * Draw disbursements — the releases against the budget above
 * (budget-scope-of-work-draws.md §4, redesign rationale §3).
 *
 * GET .../draws/{dealId} → { draws[], rollup, budgetTabEligible }. Columns come
 * from DrawDisbursement, including the model-computed advance math, which is
 * read-only and rendered in the shared grey "server computed this" tint:
 * totalEligibleAdvance, grossDrawAmount, netDrawDisbursed, totalDrawnAmount.
 *
 * The ledger stays on AG Grid — it is a read-oriented record grid, which is
 * exactly the convention's case (see the workbook's header for why that file is
 * the documented exception).
 *
 * "New draw" opens the DrilldownSidebarAddDraw panel — the one and only new-draw
 * form. It POSTs the draw itself and then calls back `onDrawCreated` to refetch.
 */
import { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CreditCardOutlined from "@mui/icons-material/CreditCardOutlined";

import { AgGridReact } from "ag-grid-react";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { EMPTY, T, cardSx, labelSx, money, num, pct } from "./budgetDrawsTheme";
import FciPayloadDialog from "./FciPayloadDialog";
import ConfirmationDialog from "../../../../components/ui/ConfirmationDialog";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { ActionCell, actionColumn } from "../../../admin/shared/adminTableActions";
import { useResolveLoanDocument } from "../LoanDrilldownWireAndFundingTab/panes/HudSourceChip";

const feesOf = (d) =>
  num(d?.inspectionFee) + num(d?.wireFee) + num(d?.additionalServicingFees);

const DrawsSection = ({
  dealId,
  draws,
  rollup,
  eligible, // computed once in the shell so this and the capital band agree
  onNewDraw, // opens the ADD_DRAW sidebar (owned by the shell)
  fetchFciPayload, // (dealId, drawId) => Promise
  onDeleteDraw, // (draw) => Promise — only the last draw (2026-10-02)
  onImportInspection, // (files, drawId?) => Promise
  importing,
}) => {
  const dispatch = useDispatch();
  const resolveDoc = useResolveLoanDocument();
  const [fciDraw, setFciDraw] = useState(null);
  const [deleteDraw, setDeleteDraw] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // One hidden file input serves the header button and every row's button;
  // `importFor` remembers which draw (if any) the report backs.
  const fileRef = useRef(null);
  const importFor = useRef(null);
  const pickReports = (drawId) => {
    importFor.current = drawId || null;
    fileRef.current?.click();
  };

  const rows = useMemo(() => draws || [], [draws]);

  // Only the LAST of our own draws can be deleted — every later draw's eligible
  // advance was computed on top of it (the backend 409s otherwise). Seller
  // (pre-purchase) draws are history, never deleted here.
  const lastDrawId = useMemo(() => {
    const own = rows.filter((d) => !d?.prePurchase);
    if (!own.length) return null;
    const last = own.reduce((a, d) =>
      num(d?.drawNumber) > num(a?.drawNumber) ? d : a,
    );
    return String(last?._id || last?.oid || "");
  }, [rows]);

  const columnDefs = useMemo(
    () => [
      {
        field: "drawNumber",
        headerName: "#",
        width: 150,
        pinned: "left",
        cellRenderer: (p) => (
          <Flx ac sx={{ gap: 0.75, height: "100%" }}>
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                width: 26,
                height: 26,
                borderRadius: "8px",
                // Pre-purchase (seller) draws are a different lender's history —
                // give them their own mark so ours aren't misread as theirs.
                bgcolor: p.data?.prePurchase ? T.muted : T.ink,
                color: "#fff",
                fontFamily: T.mono,
                fontSize: 12,
                fontWeight: 600,
                flex: "none",
              }}
            >
              {p.value}
            </Box>
            {p.data?.prePurchase && (
              <Tooltip title="Disbursed by the seller before we purchased the note" placement="top">
                <Box
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: T.muted,
                    bgcolor: "#EEF2F5",
                    borderRadius: "4px",
                    px: 0.6,
                    py: "1px",
                  }}
                >
                  Seller
                </Box>
              </Tooltip>
            )}
            {p.data?.drawKind === "day_1" && (
              <Tooltip title="Day 1 draw: funded at closing against the locked budget" placement="top">
                <Box
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: T.ink2,
                    bgcolor: "#EEF2F5",
                    borderRadius: "4px",
                    px: 0.6,
                    py: "1px",
                  }}
                >
                  Day 1
                </Box>
              </Tooltip>
            )}
            {/* Pre-close only (null on acquisitions): funded in the closing wire. */}
            {p.data?.drawDistributedOnOriginationDate === true && (
              <Tooltip
                title="Distributed on the origination date: included in the initial closing wire"
                placement="top"
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: T.accentInk,
                    bgcolor: T.accentTint,
                    borderRadius: "4px",
                    px: 0.6,
                    py: "1px",
                  }}
                >
                  Closing
                </Box>
              </Tooltip>
            )}
          </Flx>
        ),
      },
      {
        field: "date",
        headerName: "Date",
        width: 115,
        valueFormatter: (p) => formatDate(p.value, EMPTY),
      },
      {
        field: "drawRequestedAmount",
        headerName: "Requested",
        width: 118,
        type: "numericColumn",
        cellClass: "mono-cell",
        valueFormatter: (p) => money(p.value),
      },
      {
        field: "percentComplete",
        headerName: "% complete",
        width: 112,
        type: "numericColumn",
        cellClass: "mono-cell",
        valueFormatter: (p) => pct(p.value, 0),
      },
      {
        field: "totalEligibleAdvance",
        headerName: "Eligible advance",
        width: 140,
        type: "numericColumn",
        cellClass: "computed-cell",
        valueFormatter: (p) => money(p.value),
      },
      {
        field: "grossDrawAmount",
        headerName: "Gross draw",
        width: 140,
        type: "numericColumn",
        cellClass: "computed-cell",
        // The cap is the single most important thing the formula does, so it
        // gets words rather than leaving two numbers to be compared by eye.
        cellRenderer: (p) => {
          const capped = num(p.data?.drawRequestedAmount) > num(p.value) + 0.5;
          return (
            <Flx
              column
              sx={{ alignItems: "flex-end", justifyContent: "center", height: "100%", gap: 0 }}
            >
              <Box component="span" sx={{ fontFamily: T.mono }}>
                {money(p.value)}
              </Box>
              {capped && (
                <Box
                  component="span"
                  sx={{ fontSize: 12, fontWeight: 700, color: T.amber, lineHeight: 1.2 }}
                >
                  capped at eligible
                </Box>
              )}
            </Flx>
          );
        },
      },
      {
        headerName: "Fees",
        width: 100,
        type: "numericColumn",
        cellClass: "mono-cell",
        valueGetter: (p) => feesOf(p.data),
        cellRenderer: (p) => {
          const applied = num(p.data?.interestPaymentFromDraw ? p.data?.fundsAppliedToPayment : 0);
          const tip = [
            `Inspection ${money(p.data?.inspectionFee || 0)}`,
            `Wire ${money(p.data?.wireFee || 0)}`,
            `Servicing ${money(p.data?.additionalServicingFees || 0)}`,
            applied ? `Applied to interest ${money(applied)}` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <Tooltip title={tip} placement="top">
              <Box component="span" sx={{ fontFamily: T.mono }}>
                {money(p.value)}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "netDrawDisbursed",
        headerName: "Net disbursed",
        width: 132,
        type: "numericColumn",
        cellClass: "computed-cell computed-strong",
        valueFormatter: (p) => money(p.value),
      },
      {
        field: "totalDrawnAmount",
        headerName: "Total drawn",
        width: 128,
        type: "numericColumn",
        cellClass: "computed-cell",
        valueFormatter: (p) => money(p.value),
      },
      {
        headerName: "Inspection",
        flex: 1,
        minWidth: 170,
        sortable: false,
        valueGetter: (p) =>
          p.data?.inspectionVendor
            ? `${p.data.inspectionVendor}${p.data.dateOfInspection ? ` · ${formatDate(p.data.dateOfInspection, "")}` : ""}`
            : p.data?.reasonForNoInspection
              ? `No inspection: ${p.data.reasonForNoInspection}`
              : EMPTY,
        // An applied inspection stores the report as `inspectionReportFile` —
        // the text becomes the link to it (files are always previewable).
        cellRenderer: (p) => {
          const f = p.data?.inspectionReportFile;
          const doc = f
            ? resolveDoc({
                documentId: f._id || f.oid,
                fileStorageName: f.file_storage_name || f.fileStorageName,
              }) || (f.file_storage_name ? f : null)
            : null;
          return (
            <Flx ac sx={{ height: "100%", minWidth: 0 }}>
              {doc ? (
                <Box
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(filePreviewSetData(doc));
                  }}
                  title="Open the inspection report"
                  sx={{
                    border: "none",
                    background: "transparent",
                    p: 0,
                    font: "inherit",
                    fontSize: "12px",
                    color: T.accentInk,
                    cursor: "pointer",
                    textAlign: "left",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {p.value}
                </Box>
              ) : (
                <Box component="span" sx={{ color: T.muted, fontSize: "12px" }}>
                  {p.value}
                </Box>
              )}
            </Flx>
          );
        },
      },
      {
        headerName: "",
        width: 118,
        sortable: false,
        filter: false,
        pinned: "right",
        cellRenderer: (p) => (
          <Button
            variant="text"
            size="small"
            onClick={() => setFciDraw(p.data)}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            FCI payload
          </Button>
        ),
      },
      ...(onDeleteDraw || onImportInspection
        ? [
            actionColumn({
              colId: "drawActions",
              count: 2,
              cellRenderer: (p) => {
                const id = String(p.data?._id || p.data?.oid || "");
                const isLast = !!id && id === lastDrawId;
                return (
                  <ActionCell>
                    <Tooltip title="Import a site inspection for this draw">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!onImportInspection || importing || p.data?.prePurchase}
                          onClick={(e) => {
                            e.stopPropagation();
                            pickReports(id);
                          }}
                        >
                          <FactCheckOutlined sx={{ fontSize: 17 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        p.data?.prePurchase
                          ? "Seller draws can't be deleted"
                          : isLast
                            ? "Delete this draw"
                            : "Only the last draw can be deleted — later draws were computed on top of this one"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!onDeleteDraw || !isLast}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDraw(p.data);
                          }}
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 17 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ActionCell>
                );
              },
            }),
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastDrawId, importing, resolveDoc, onDeleteDraw, onImportInspection],
  );

  const progression = rollup?.principalBalanceProgression || [];
  const gridHeight = `${Math.min(Math.max(rows.length, 1) * 44 + 52, 440)}px`;

  return (
    <Box sx={cardSx}>
      {/* Header */}
      <Flx
        jb
        ac
        wrap
        sx={{ gap: 1.5, p: "16px 20px", borderBottom: `1px solid ${T.line2}` }}
      >
        <Flx ac sx={{ gap: 1.5 }}>
          <Txt sx={{ fontSize: 15.5, fontWeight: 700, color: T.ink, letterSpacing: "-0.01em" }}>
            Draw Disbursements
          </Txt>
          {rows.length > 0 && (
            <Txt sx={{ fontSize: 12, color: T.muted }}>
              {rows.length} holdback {rows.length === 1 ? "release" : "releases"}
              {rollup?.prePurchaseDrawCount
                ? ` · ${rollup.prePurchaseDrawCount} pre-purchase`
                : ""}
            </Txt>
          )}
        </Flx>
        <Flx ac sx={{ gap: 1 }}>
          {onImportInspection && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FactCheckOutlined sx={{ fontSize: 16 }} />}
              disabled={importing}
              onClick={() => pickReports(null)}
              sx={{ textTransform: "none" }}
            >
              {importing ? "Uploading…" : "Import site inspection"}
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<AddRounded sx={{ fontSize: 17 }} />}
            onClick={onNewDraw}
            sx={{
              textTransform: "none",
              bgcolor: T.accent,
              "&:hover": { bgcolor: T.accentInk },
            }}
          >
            New draw
          </Button>
        </Flx>
        <input
          ref={fileRef}
          type="file"
          hidden
          multiple
          accept=".pdf,image/*"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            e.target.value = "";
            if (files.length && onImportInspection) {
              onImportInspection(files, importFor.current);
            }
          }}
        />
      </Flx>

      {/* Eligible-advance strip — the question this section exists to answer. */}
      <Flx
        jb
        ac
        wrap
        sx={{
          gap: 1.75,
          p: "13px 20px",
          bgcolor: T.accentTint,
          borderBottom: `1px solid ${T.accentTint2}`,
        }}
      >
        <Txt sx={{ fontSize: 12, color: T.accentInk }}>
          {eligible?.lead}{" "}
          <Box component="span" sx={{ fontFamily: T.mono, fontWeight: 600 }}>
            {pct(eligible?.basis, eligible?.digits ?? 1)}
          </Box>{" "}
          complete, up to{" "}
          <Box component="span" sx={{ fontFamily: T.mono, fontWeight: 600 }}>
            {money(eligible?.amount)}
          </Box>{" "}
          {eligible?.tail}
        </Txt>
        {/* Printing the formula turns a mystery number into an auditable one. */}
        <Txt sx={{ fontSize: 12, color: T.accentInk, opacity: 0.85 }}>
          eligible advance = % complete × holdback − drawn to date
        </Txt>
      </Flx>

      {/* Ledger */}
      {rows.length === 0 ? (
        <Box sx={{ p: "52px 24px", textAlign: "center" }}>
          <Flx
            jc
            sx={{
              width: 44,
              height: 44,
              mx: "auto",
              mb: 1.75,
              borderRadius: "12px",
              bgcolor: T.accentTint,
              alignItems: "center",
              color: T.accentInk,
            }}
          >
            <CreditCardOutlined sx={{ fontSize: 20 }} />
          </Flx>
          <Txt sx={{ display: "block", fontSize: 15, fontWeight: 700, color: T.ink, mb: 0.6 }}>
            No draws yet
          </Txt>
          <Txt
            sx={{
              display: "block",
              fontSize: 12,
              color: T.muted,
              maxWidth: 380,
              mx: "auto",
              mb: 2.25,
            }}
          >
            When work is inspected and verified, create the first draw request to release funds
            from the holdback.
          </Txt>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<AddRounded sx={{ fontSize: 17 }} />}
            onClick={onNewDraw}
            sx={{ textTransform: "none", bgcolor: T.accent, "&:hover": { bgcolor: T.accentInk } }}
          >
            Create first draw
          </Button>
        </Box>
      ) : (
        <AgGridTableWrapper
          height={gridHeight}
          noBorder
          sx={{
            "& .mono-cell": { fontFamily: T.mono, fontSize: "12px" },
            "& .computed-cell": {
              background: T.computed,
              fontFamily: T.mono,
              fontSize: "12px",
              color: T.ink2,
            },
            "& .computed-strong": { fontWeight: 600, color: T.ink },
          }}
        >
          <AgGridReact
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, sortable: true }}
            getRowId={(p) =>
              String(
                p.data._id ||
                  p.data.oid ||
                  `${p.data.prePurchase ? "pre" : "own"}-${p.data.drawNumber}`,
              )
            }
          />
        </AgGridTableWrapper>
      )}

      {/* Principal-balance progression — the trajectory, anchored at close so the
          chain ties out end to end: base → +gross per draw → current principal. */}
      {progression.length > 0 && (
        <Box sx={{ p: "16px 20px 20px", borderTop: `1px solid ${T.line2}` }}>
          <Txt sx={{ ...labelSx, fontSize: 12, display: "block", mb: 1.5 }}>
            Principal balance progression
          </Txt>
          <Flx sx={{ gap: 0, overflowX: "auto", pb: 0.5, alignItems: "stretch" }}>
            <Box
              sx={{
                flex: "none",
                border: `1px solid ${T.line}`,
                borderRadius: T.radiusSm,
                p: "10px 14px",
                bgcolor: "#F6F8FA",
                minWidth: 138,
              }}
            >
              <Txt sx={{ ...labelSx, fontSize: 12, display: "block" }}>At close</Txt>
              <Txt
                sx={{
                  display: "block",
                  fontFamily: T.mono,
                  fontSize: 12,
                  fontWeight: 600,
                  color: T.ink,
                  mt: 0.25,
                }}
              >
                {money(rollup?.baseLoanAmount)}
              </Txt>
              <Txt sx={{ display: "block", fontSize: 12, color: T.muted }}>
                Base loan funded
              </Txt>
            </Box>

            {progression.map((s, i) => (
              <Flx key={`${s.prePurchase ? "pre" : "own"}-${s.drawNumber ?? i}`} sx={{ alignItems: "stretch" }}>
                <Flx jc ac sx={{ width: 26, flex: "none", color: T.faint }}>
                  <ArrowForwardRounded sx={{ fontSize: 12 }} />
                </Flx>
                <Box
                  sx={{
                    flex: "none",
                    border: `1px solid ${s.prePurchase ? T.line : T.line}`,
                    borderRadius: T.radiusSm,
                    p: "10px 14px",
                    bgcolor: s.prePurchase ? "#F6F8FA" : "#fff",
                    minWidth: 138,
                  }}
                >
                  <Txt sx={{ ...labelSx, fontSize: 12, display: "block" }}>
                    {s.prePurchase ? "Seller draw" : "Draw"} {s.drawNumber} ·{" "}
                    {formatDate(s.date, EMPTY)}
                  </Txt>
                  <Txt
                    sx={{
                      display: "block",
                      fontFamily: T.mono,
                      fontSize: 12,
                      fontWeight: 600,
                      color: T.ink,
                      mt: 0.25,
                    }}
                  >
                    {money(s.principalBalanceAfter)}
                  </Txt>
                  <Txt sx={{ display: "block", fontSize: 12, color: T.muted }}>
                    Principal after
                  </Txt>
                  <Txt
                    sx={{
                      display: "block",
                      fontFamily: T.mono,
                      fontSize: 12,
                      color: T.accentInk,
                      mt: 0.4,
                    }}
                  >
                    +{money(s.netDrawDisbursed)} net
                  </Txt>
                </Box>
              </Flx>
            ))}
          </Flx>
        </Box>
      )}

      <ConfirmationDialog
        open={!!deleteDraw}
        title={`Delete draw #${deleteDraw?.drawNumber ?? ""} for ${money(deleteDraw?.drawRequestedAmount)}?`}
        message="The draw is archived (it can be restored) and drawn-to-date and the remaining holdback are recomputed without it."
        confirmText="Delete draw"
        destructive
        loading={deleting}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await onDeleteDraw(deleteDraw);
          } finally {
            setDeleting(false);
            setDeleteDraw(null);
          }
        }}
        onCancel={() => setDeleteDraw(null)}
      />

      <FciPayloadDialog
        open={!!fciDraw}
        onClose={() => setFciDraw(null)}
        dealId={dealId}
        draw={fciDraw}
        fetchPayload={fetchFciPayload}
      />
    </Box>
  );
};

export default DrawsSection;
